import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { supervisorApi } from "@/lib/SupervisorApi";
import { Field, inputCls } from "./SupervisorPrimitives";

/**
 * Full-screen block that intercepts a supervisor whose account has
 * ``must_change_password`` set (SEC-002 forced rotation on first login).
 * Non-dismissable except via logout — the panel remains hidden behind
 * a heavy backdrop until the operator either rotates or logs out.
 */
export default function ForcePasswordChangeDialog({ onDone, onLogout }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (newPw !== confirm) { toast.error("Τα passwords δεν ταιριάζουν"); return; }
    if (newPw.length < 12) { toast.error("Τουλάχιστον 12 χαρακτήρες"); return; }
    setBusy(true);
    try {
      await supervisorApi.changePassword(oldPw, newPw);
      toast.success("Το password αλλάχθηκε επιτυχώς");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur grid place-items-center z-[60] p-4">
      <div className="bg-zinc-900 border-2 border-amber-600 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-amber-900/50 bg-amber-950/30">
          <div className="font-bold text-lg text-amber-300 flex items-center gap-2">
            <AlertTriangle size={18} /> Υποχρεωτική Αλλαγή Password
          </div>
          <div className="text-xs text-amber-200/70 mt-1">
            Για ασφάλεια, πρέπει να ορίσετε νέο password πριν προχωρήσετε.
          </div>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Τρέχον Password">
            <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} className={inputCls} data-testid="force-pw-old" autoFocus />
          </Field>
          <Field label="Νέο Password (≥12 χαρακτήρες, μικρά+κεφαλαία+αριθμοί)">
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} data-testid="force-pw-new" />
          </Field>
          <Field label="Επιβεβαίωση Νέου Password">
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="force-pw-confirm" />
          </Field>
          <div className="flex gap-2 justify-end pt-3 border-t border-zinc-800">
            <button onClick={onLogout} className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-bold" data-testid="force-pw-logout">
              Έξοδος
            </button>
            <button onClick={submit} disabled={busy || !oldPw || !newPw} data-testid="force-pw-submit"
              className="h-10 px-4 rounded-lg bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 text-white text-sm font-bold disabled:opacity-50">
              {busy ? "..." : "Αλλαγή"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
